import React, { useEffect } from 'react';
import { BadgeCheck, CalendarClock, CreditCard, FileText, HelpCircle, ShieldCheck } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SubscribeButton } from '../../../components/billing/SubscribeButton';
import { Card } from '../../../components/ui/Card';
import { SectionEyebrow } from '../../../components/ui/SectionEyebrow';
import { useAuth } from '../../../lib/auth/AuthContext';

function planLabel(tier: string | null | undefined, active: boolean | undefined): string {
  if (!active) return 'Free';
  if (tier === 'b2b') return 'Business';
  return 'Premium';
}

export const BillingScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, profileLoading, refreshProfile } = useAuth();

  const active = profile?.subscription_active === true;
  const tierLabel = planLabel(profile?.subscription_tier, active);
  const statusLabel = active ? 'Active' : 'No active subscription';
  const customerId = profile?.stripe_customer_id ?? null;

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get('checkout') !== 'success') return;
    void refreshProfile().finally(() => {
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
    });
  }, [location.pathname, location.search, navigate, refreshProfile]);

  return (
    <div className="w-full max-w-5xl space-y-5 bg-background px-4 pb-10 pt-6 sm:px-6 lg:mx-auto lg:px-8 lg:pt-8">
      <header className="space-y-2">
        <SectionEyebrow>Billing</SectionEyebrow>
        <h1 className="text-xl font-semibold text-text sm:text-2xl">Manage subscription</h1>
        <p className="text-sm text-textSecondary">Plan details, payment status, and billing history in one place.</p>
      </header>

      <Card tone="default" className="transition duration-300 hover:-translate-y-0.5">
        <div className="mb-4 flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15 text-primary" aria-hidden>
            <BadgeCheck className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-text">Current plan</p>
            <p className="text-xs text-textMuted">
              {profileLoading ? 'Loading plan…' : `${tierLabel} · ${statusLabel}`}
            </p>
          </div>
        </div>
        <div className="space-y-3 rounded-2xl border border-borderSoft/40 bg-white/80 px-4 py-4 sm:px-5">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-textMuted">Plan tier</span>
            <span className="font-medium text-text">{profileLoading ? 'Loading…' : tierLabel}</span>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-textMuted">Status</span>
            <span className={`font-medium ${active ? 'text-brandTeal' : 'text-text'}`}>{profileLoading ? 'Loading…' : statusLabel}</span>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-textMuted">Stripe customer</span>
            <span className="max-w-[65%] truncate font-mono text-xs text-text">{customerId ?? 'Not linked yet'}</span>
          </div>
          {!active ? (
            <SubscribeButton
              label="Upgrade to Premium"
              variant="primary"
              size="md"
              fullWidth
              successUrl={`${window.location.origin}/app/billing?checkout=success`}
              cancelUrl={`${window.location.origin}/app/billing?checkout=cancel`}
            />
          ) : (
            <p className="text-xs leading-relaxed text-textMuted">
              You currently have full access. Customer Portal cancellation/manage-payment can be added next.
            </p>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card tone="default" className="transition duration-300 hover:-translate-y-0.5">
          <div className="mb-4 flex items-start gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15 text-primary" aria-hidden>
              <CalendarClock className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-text">Renewal and changes</p>
              <p className="text-xs text-textMuted">Billing cycle and cancellation behavior.</p>
            </div>
          </div>
          <div className="space-y-2 rounded-2xl border border-borderSoft/40 bg-white/80 px-4 py-4 sm:px-5">
            <p className="text-sm text-textSecondary">
              Renewal date and cancellation-at-period-end status are available once Customer Portal integration is enabled.
            </p>
            <p className="text-xs text-textMuted">
              Current fallback: access is synced from Stripe webhooks and reflected in your profile.
            </p>
          </div>
        </Card>

        <Card tone="default" className="transition duration-300 hover:-translate-y-0.5">
          <div className="mb-4 flex items-start gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15 text-primary" aria-hidden>
              <CreditCard className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-text">Payment method</p>
              <p className="text-xs text-textMuted">Card details and updates.</p>
            </div>
          </div>
          <div className="space-y-2 rounded-2xl border border-borderSoft/40 bg-white/80 px-4 py-4 sm:px-5">
            <p className="text-sm text-textSecondary">Managed securely by Stripe Checkout/Customer Portal.</p>
            <p className="text-xs text-textMuted">No payment card details are stored in Newrotune.</p>
          </div>
        </Card>

        <Card tone="default" className="transition duration-300 hover:-translate-y-0.5">
          <div className="mb-4 flex items-start gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15 text-primary" aria-hidden>
              <FileText className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-text">Invoices</p>
              <p className="text-xs text-textMuted">Receipts and invoice history.</p>
            </div>
          </div>
          <div className="space-y-2 rounded-2xl border border-borderSoft/40 bg-white/80 px-4 py-4 sm:px-5">
            <p className="text-sm text-textSecondary">Invoice list will appear here after Customer Portal is connected.</p>
          </div>
        </Card>

        <Card tone="default" className="transition duration-300 hover:-translate-y-0.5">
          <div className="mb-4 flex items-start gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15 text-primary" aria-hidden>
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-text">Security and support</p>
              <p className="text-xs text-textMuted">Billing questions and troubleshooting.</p>
            </div>
          </div>
          <div className="space-y-2 rounded-2xl border border-borderSoft/40 bg-white/80 px-4 py-4 sm:px-5">
            <p className="text-sm text-textSecondary">
              Subscription state updates automatically from Stripe webhook events.
            </p>
            <p className="inline-flex items-center gap-1 text-xs text-textMuted">
              <HelpCircle className="h-3.5 w-3.5" aria-hidden /> Need help? Contact support from your account email.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
